/**
 * DeviceHeader.jsx (868m4jf4v)
 *
 * Shared identity header for a device detail page -- back link, category
 * icon, title + online/offline badge, the manufacturer/model/serial/IP
 * identity line, an optional secondary line (e.g. "Monitored via X"), and
 * an actions slot (children) for the page's own control pills/buttons.
 *
 * Replaces the independently hand-styled header blocks in
 * MdSwitchDetail.jsx (switch + access point variants) and
 * MdRouterDetail.jsx (router variant). Owns the flex-wrap behavior at
 * every level (outer row, identity-parts row, actions row) so a page using
 * this component cannot reproduce the mobile clipping bug fixed earlier in
 * this ticket -- that fix now lives here structurally, not per page.
 *
 * The status badge and icon use theme-tokens.css's own --status-ok/
 * --status-ok-bg/--status-ok-text tokens for "online", never a hardcoded
 * green -- and a neutral --text-tertiary/--bg-page treatment for "offline"
 * (not --status-error/red: offline is not a fault state on its own, same
 * reasoning MdRouterDetail.jsx's WAN_STATE map already applies to
 * DISABLED vs DISCONNECTED).
 */

export default function DeviceHeader({
  icon: Icon,
  online,
  title,
  identityParts = [],
  secondaryLine,
  backLabel = 'Network',
  onBack,
  children,
}) {
  const parts = identityParts.filter((p) => p !== null && p !== undefined && p !== '');

  return (
    <div className="dd-header">
      <div className="dd-header-identity">
        <button type="button" className="dd-header-back" onClick={onBack}>
          <span aria-hidden="true">←</span> {backLabel}
        </button>
        <span className="dd-header-divider">/</span>
        {Icon && (
          <div className={`dd-header-icon ${online ? 'dd-header-icon-online' : 'dd-header-icon-offline'}`}>
            <Icon size={18} />
          </div>
        )}
        <div className="dd-header-text">
          <div className="dd-header-title-row">
            <span className="dd-header-title">{title}</span>
            <span className={`dd-header-badge ${online ? 'dd-header-badge-online' : 'dd-header-badge-offline'}`}>
              {online ? 'ONLINE' : 'OFFLINE'}
            </span>
          </div>
          {parts.length > 0 && (
            <div className="dd-header-identity-line">
              {parts.map((part, i) => (
                <span key={i}>
                  {part}
                  {i < parts.length - 1 ? ' ·' : ''}
                </span>
              ))}
            </div>
          )}
          {secondaryLine && <div className="dd-header-secondary-line">{secondaryLine}</div>}
        </div>
      </div>
      <div className="dd-header-actions">
        {children}
      </div>
    </div>
  );
}
